import { thresholdsMap } from '../../hooks/useAttractionThresholds';

export type WaitStatus = 'Walk On' | 'Short' | 'Moderate' | 'Long' | 'Avoid' | 'Closed' | 'Unknown';

export interface ThresholdConfig {
  walkOn: number;
  short: number;
  moderate: number;
  long: number;
}

export const DEFAULT_THRESHOLDS: ThresholdConfig = { walkOn: 10, short: 20, moderate: 35, long: 55 };

/**
 * Merged thresholds: hardcoded config + DB-fetched values.
 * DB values take precedence. Populated by useAttractionThresholds on fetch.
 */
function getMergedThresholds(): Record<string, ThresholdConfig> {
  return { ...thresholdsMap };
}

export function waitStatus(minutes: number | null, attractionId?: string): WaitStatus {
  if (minutes === null || minutes === undefined) return 'Closed';
  const merged = getMergedThresholds();
  const t = attractionId ? (merged[attractionId] ?? DEFAULT_THRESHOLDS) : DEFAULT_THRESHOLDS;
  if (minutes <= t.walkOn) return 'Walk On';
  if (minutes <= t.short) return 'Short';
  if (minutes <= t.moderate) return 'Moderate';
  if (minutes <= t.long) return 'Long';
  return 'Avoid';
}

/**
 * Composite status that merges absolute threshold status with relative park-wide rank.
 *
 * When an attraction's absolute status is severe (Avoid / Long) but it ranks well
 * within its park (shortest waits), the status is downgraded to avoid steering
 * users away from rides that are actually good relative options.
 *
 * Rules:
 *   - Avoid + rank <= total * 0.25 → Long  (top 25% = still worth considering)
 *   - Avoid + rank <= total * 0.50 → Moderate (top 50% = decent option)
 *   - Long + rank === 1            → Short  (best in park = down two levels)
 *   - Long + rank === 2            → Moderate (2nd best = down one level)
 *   - Otherwise → absolute status
 */
export function compositeStatus(
  minutes: number | null,
  attractionId: string | undefined,
  /** 1-based rank within park (1 = shortest wait) */
  parkRank: number | null,
  /** total operating attractions in the park */
  parkTotal: number | null,
): WaitStatus {
  const absolute = waitStatus(minutes, attractionId);

  // No park context → fall back to absolute
  if (parkRank == null || parkTotal == null || parkTotal < 2) return absolute;

  // "Avoid" downgraded by park rank
  if (absolute === 'Avoid') {
    if (parkRank <= Math.ceil(parkTotal * 0.25)) return 'Long';
    if (parkRank <= Math.ceil(parkTotal * 0.50)) return 'Moderate';
    return 'Avoid';
  }

  // "Long" downgraded by park rank
  if (absolute === 'Long') {
    if (parkRank === 1) return 'Short';
    if (parkRank === 2) return 'Moderate';
    return 'Long';
  }

  return absolute;
}
